import { useState, useEffect } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import { ru } from 'date-fns/locale';
import { format, addDays } from 'date-fns';
import 'react-datepicker/dist/react-datepicker.css';
import { Button } from '../ui/Button';
import { getCurrentUser } from '../../services/telegram-auth';
import { useNavigate } from 'react-router-dom';
import { createBooking, getUserDiscount } from '../../services/supabase';
import { toast } from 'react-hot-toast';

// Регистрируем русскую локализацию
registerLocale('ru', ru);

// Интерфейс для выбранного оборудования
interface SelectedItem {
    id: string;
    name: string;
    daily_rate: number;
    quantity: number;
}

// Определяем интерфейс для пропсов компонента
interface BookingWidgetProps {
    dailyRate: number;
    equipmentId: string;
    status: 'available' | 'maintenance' | 'booked';
    // Добавляем поддержку множественного выбора
    multipleSelection?: boolean;
    selectedEquipment?: SelectedItem[];
}

const BookingWidget: React.FC<BookingWidgetProps> = ({
    dailyRate,
    equipmentId,
    status,
    multipleSelection = false,
    selectedEquipment = []
}) => {
    // Добавляем навигацию для перенаправления на страницу входа
    const navigate = useNavigate();

    // Добавляем состояние для отслеживания авторизации пользователя
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);

    // Состояния для выбранных дат и количества гостей
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [guests, setGuests] = useState<number>(1);
    const [totalPrice, setTotalPrice] = useState<number>(0);
    const [userDiscount, setUserDiscount] = useState<number>(0);

    // Проверяем авторизацию пользователя при загрузке компонента
    useEffect(() => {
        const currentUser = getCurrentUser();
        setIsAuthenticated(!!currentUser);
        console.log('Статус авторизации:', !!currentUser);
    }, []);

    // Получаем информацию о скидке пользователя при монтировании компонента
    useEffect(() => {
        const fetchUserDiscount = async () => {
            const currentUser = getCurrentUser();
            if (currentUser) {
                try {
                    const { data, error } = await getUserDiscount();
                    if (error) {
                        console.error('Ошибка при получении скидки:', error);
                    } else if (data) {
                        setUserDiscount(data);
                        console.log('Скидка пользователя:', data);
                    }
                } catch (error) {
                    console.error('Ошибка при получении скидки:', error);
                }
            }
        };
        
        fetchUserDiscount();
    }, []);

    // Вычисляем общую стоимость при изменении дат
    useEffect(() => {
        if (startDate && endDate) {
            const days = Math.floor((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1 || 1;
            
            // Рассчитываем стоимость с учетом скидки
            let price = 0;
            
            if (multipleSelection) {
                // Для множественного выбора оборудования
                price = selectedEquipment.reduce((sum, item) => {
                    return sum + (item.daily_rate * item.quantity * days);
                }, 0);
            } else {
                // Для одного оборудования
                price = days * dailyRate * guests;
            }
            
            // Применяем скидку, если она есть
            if (userDiscount > 0) {
                price = price * (1 - userDiscount / 100);
            }
            
            setTotalPrice(price);
        } else {
            setTotalPrice(0);
        }
    }, [startDate, endDate, dailyRate, guests, selectedEquipment, multipleSelection, userDiscount]);

    // Обработчик выбора даты начала
    const handleStartDateChange = (date: Date | null) => {
        setStartDate(date);
        // Если выбрана дата начала и она позже текущей даты окончания или дата окончания не выбрана,
        // устанавливаем дату окончания на следующий день после даты начала
        if (date && (!endDate || date >= endDate)) {
            setEndDate(addDays(date, 1));
        }
    };

    // Обработчик выбора даты окончания
    const handleEndDateChange = (date: Date | null) => {
        setEndDate(date);
    };

    // Обработчик изменения количества гостей
    const handleGuestsChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setGuests(parseInt(e.target.value, 10));
    };

    // Обработчик бронирования
    const handleBooking = async () => {
        if (!startDate || !endDate) return;

        // Проверяем авторизацию пользователя
        const currentUser = getCurrentUser();
        if (!currentUser) {
            // Если пользователь не авторизован, сохраняем данные бронирования в localStorage
            // и перенаправляем на страницу входа
            const bookingIntent = {
                startDate: startDate.toISOString(),
                endDate: endDate.toISOString(),
                guests,
                totalPrice,
                equipment: multipleSelection
                    ? selectedEquipment.map(item => item.id)
                    : [equipmentId],
                returnUrl: window.location.pathname
            };

            localStorage.setItem('bookingIntent', JSON.stringify(bookingIntent));

            // Показываем сообщение о необходимости входа и перенаправляем
            alert('Для бронирования необходимо войти в систему. Пожалуйста, авторизуйтесь.');
            navigate('/login');
            return; // Важно прервать выполнение функции здесь!
        }

        try {
            // Подготовка данных для отправки (только для авторизованного пользователя)
            const bookingData = {
                user_id: currentUser.telegram_id,
                start_date: startDate.toISOString(),
                end_date: endDate.toISOString(),
                total_price: totalPrice,
                guests: guests,
                equipment_items: multipleSelection
                    ? selectedEquipment.map(item => ({
                        equipment_id: item.id,
                        quantity: item.quantity,
                        daily_rate: item.daily_rate
                    }))
                    : [{
                        equipment_id: equipmentId,
                        quantity: 1,
                        daily_rate: dailyRate
                    }]
            };

            // Отправляем запрос на бронирование через API
            const { data, error } = await createBooking(bookingData);

            if (error) {
                console.error('Ошибка при бронировании:', error);

                // Проверяем, связана ли ошибка с авторизацией
                const errorMessage = typeof error === 'string'
                    ? error
                    : error.message || 'Произошла ошибка при бронировании';

                if (errorMessage && (
                    errorMessage.includes('авторизац') ||
                    errorMessage.includes('auth') ||
                    errorMessage.toLowerCase().includes('login') ||
                    !currentUser
                )) {
                    // Если ошибка связана с авторизацией, сохраняем намерение и перенаправляем
                    const bookingIntent = {
                        startDate: startDate.toISOString(),
                        endDate: endDate.toISOString(),
                        guests,
                        totalPrice,
                        equipment: multipleSelection
                            ? selectedEquipment.map(item => item.id)
                            : [equipmentId],
                        returnUrl: window.location.pathname
                    };

                    localStorage.setItem('bookingIntent', JSON.stringify(bookingIntent));

                    toast.error('Для бронирования необходимо войти в систему', {
                        icon: '🔒',
                        duration: 4000,
                        style: {
                            background: 'linear-gradient(135deg, #f0f9ff 0%, #e1f5fe 100%)',
                            color: '#0277bd',
                            border: '1px solid #b3e5fc',
                            padding: '16px',
                            fontWeight: '500'
                        }
                    });
                    setTimeout(() => {
                        navigate('/login');
                    }, 2500);
                    return;
                }

                // Другие ошибки
                toast.error(typeof error === 'string'
                    ? error
                    : error.message || 'Произошла ошибка при бронировании');
                return;
            }

            console.log('Бронирование успешно создано:', data);
            toast.success('Бронирование успешно создано!', {
                icon: '✅',
                duration: 4000,
                style: {
                    background: 'linear-gradient(135deg, #f0fff4 0%, #e8f5e9 100%)',
                    color: '#2e7d32',
                    border: '1px solid #c8e6c9',
                    padding: '16px',
                    fontWeight: '500'
                }
            });

            // Перенаправляем на страницу профиля с историей бронирований
            navigate('/profile');
        } catch (error) {
            console.error('Ошибка при бронировании:', error);

            // Проверяем, не связана ли ошибка с авторизацией
            const errorMessage = error instanceof Error ? error.message : String(error);
            if (errorMessage.toLowerCase().includes('auth') ||
                errorMessage.toLowerCase().includes('login') ||
                errorMessage.includes('авторизац') ||
                !getCurrentUser()) {

                const bookingIntent = {
                    startDate: startDate.toISOString(),
                    endDate: endDate.toISOString(),
                    guests,
                    totalPrice,
                    equipment: multipleSelection
                        ? selectedEquipment.map(item => item.id)
                        : [equipmentId],
                    returnUrl: window.location.pathname
                };

                localStorage.setItem('bookingIntent', JSON.stringify(bookingIntent));

                toast.error('Для бронирования необходимо войти в систему', {
                    icon: '🔒',
                    duration: 4000,
                    style: {
                        background: 'linear-gradient(135deg, #f0f9ff 0%, #e1f5fe 100%)',
                        color: '#0277bd',
                        border: '1px solid #b3e5fc',
                        padding: '16px',
                        fontWeight: '500'
                    }
                });
                setTimeout(() => {
                    navigate('/login');
                }, 2500);
                return;
            }

            toast.error('Произошла ошибка при бронировании. Пожалуйста, попробуйте снова позже.', {
                icon: '❌',
                duration: 4000,
                style: {
                    background: 'linear-gradient(135deg, #fff5f5 0%, #ffe1e1 100%)',
                    color: '#e53935',
                    border: '1px solid #ffcdd2',
                    padding: '16px',
                    fontWeight: '500'
                }
            });
        }
    };

    // Обработчик для кнопки авторизации
    const handleAuthRedirect = () => {
        // Сохраняем информацию о текущей странице для возврата после авторизации
        localStorage.setItem('authRedirectUrl', window.location.pathname);
        navigate('/login');
    };

    // Форматируем дату для отображения
    const formatDateDisplay = (date: Date | null): string => {
        if (!date) return '';
        return format(date, 'dd.MM.yyyy', { locale: ru });
    };

    // Расчет исходной стоимости (без скидки)
    const calculateOriginalPrice = () => {
        if (!startDate || !endDate) return 0;
        
        const days = Math.floor((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1 || 1;
        
        if (multipleSelection) {
            return selectedEquipment.reduce((sum, item) => {
                return sum + (item.daily_rate * item.quantity * days);
            }, 0);
        } else {
            return days * dailyRate * guests;
        }
    };
    
    // Получаем исходную цену и рассчитываем сумму скидки
    const originalPrice = calculateOriginalPrice();
    const discountAmount = userDiscount > 0 ? (originalPrice * userDiscount / 100) : 0;

    return (
        <div className={multipleSelection ? "" : "bg-white rounded-lg shadow-sm border border-gray-200 p-6"}>
            <h2 className="text-xl font-semibold mb-6">{multipleSelection ? "Забронировать выбранное оборудование" : "Бронирование"}</h2>

            {status !== 'available' && !multipleSelection ? (
                <div className="bg-amber-50 text-amber-700 p-4 rounded-md mb-4">
                    {status === 'booked'
                        ? 'Данное оборудование уже забронировано на выбранные даты.'
                        : 'Данное оборудование находится на обслуживании.'}
                </div>
            ) : !isAuthenticated ? (
                // Блок для неавторизованных пользователей
                <div className="bg-blue-50 p-4 rounded-md mb-4 border border-blue-100">
                    <h3 className="text-blue-800 font-medium mb-2">Требуется авторизация</h3>
                    <p className="text-blue-700 mb-4 text-sm">
                        Для бронирования оборудования необходимо войти в систему или зарегистрироваться.
                    </p>
                    <Button
                        onClick={handleAuthRedirect}
                        className="w-full"
                    >
                        Войти в систему
                    </Button>
                </div>
            ) : (
                <>
                    {/* Список выбранного оборудования при множественном выборе */}
                    {multipleSelection && selectedEquipment.length > 0 && (
                        <div className="mb-4">
                            <h3 className="font-medium text-sm text-gray-700 mb-2">Выбранное оборудование:</h3>
                            <ul className="space-y-1 mb-4">
                                {selectedEquipment.map(item => (
                                    <li key={item.id} className="flex justify-between items-center">
                                        <span className="text-sm">
                                            {item.name} <span className="text-gray-500">({item.quantity} шт.)</span>
                                        </span>
                                        <span className="text-sm font-medium">
                                            {(item.daily_rate !== undefined && item.daily_rate !== null && item.daily_rate > 0) 
                                                ? `${(item.daily_rate * item.quantity).toLocaleString()} ₽/день`
                                                : 'Цена не указана'}
                                        </span>
                                    </li>
                                ))}
                                <li className="flex justify-between items-center pt-2 border-t border-gray-200 mt-2">
                                    <span className="font-medium">Итого за день:</span>
                                    <span className="font-medium text-primary">{dailyRate.toLocaleString()} ₽</span>
                                </li>
                            </ul>
                        </div>
                    )}

                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Дата начала аренды
                        </label>
                        <DatePicker
                            selected={startDate}
                            onChange={handleStartDateChange}
                            selectsStart
                            startDate={startDate}
                            endDate={endDate}
                            minDate={new Date()}
                            locale="ru"
                            dateFormat="dd.MM.yyyy"
                            placeholderText="Выберите дату начала"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-300"
                        />
                    </div>

                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Дата окончания аренды
                        </label>
                        <DatePicker
                            selected={endDate}
                            onChange={handleEndDateChange}
                            selectsEnd
                            startDate={startDate}
                            endDate={endDate}
                            minDate={startDate || new Date()}
                            locale="ru"
                            dateFormat="dd.MM.yyyy"
                            placeholderText="Выберите дату окончания"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-300"
                        />
                    </div>

                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Количество человек
                        </label>
                        <select
                            value={guests}
                            onChange={handleGuestsChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-300"
                        >
                            {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                                <option key={num} value={num}>
                                    {num} {num === 1 ? 'человек' : num < 5 ? 'человека' : 'человек'}
                                </option>
                            ))}
                        </select>
                    </div>

                    {startDate && endDate && (
                        <div className="bg-gray-50 p-4 rounded-md mb-6">
                            <div className="flex justify-between mb-2">
                                <span className="text-gray-600">Период аренды:</span>
                                <span className="font-medium">
                                    {formatDateDisplay(startDate)} - {formatDateDisplay(endDate)}
                                </span>
                            </div>
                            <div className="flex justify-between mb-2">
                                <span className="text-gray-600">Стоимость за день:</span>
                                <span className="font-medium">{dailyRate.toLocaleString()} ₽</span>
                            </div>
                            {userDiscount > 0 && (
                                <div className="flex justify-between mb-2">
                                    <span className="text-green-600">Ваша скидка ({userDiscount}%):</span>
                                    <span className="font-medium text-green-600">-{discountAmount.toLocaleString()} ₽</span>
                                </div>
                            )}
                            <div className="flex justify-between text-lg font-semibold mt-2 pt-2 border-t border-gray-200">
                                <span>Итого за весь период:</span>
                                <span className="text-primary">{totalPrice.toLocaleString()} ₽</span>
                            </div>
                        </div>
                    )}

                    <Button
                        onClick={handleBooking}
                        disabled={!startDate || !endDate}
                        className="w-full"
                    >
                        Забронировать {multipleSelection ? `(${selectedEquipment.length} ${selectedEquipment.length === 1 ? 'позиция' : 'позиции'}, ${selectedEquipment.reduce((sum, item) => sum + item.quantity, 0)} шт.)` : ''}
                    </Button>
                </>
            )}
        </div>
    );
};

export default BookingWidget; 